import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useRef } from 'react';
import { offset } from '@floating-ui/core';
import { useFloating, useFocus, useHover, useInteractions, useClick, useDismiss, flip, arrow as arrowMiddleware, FloatingArrow, FloatingNode, FloatingTree, useFloatingNodeId, useFloatingParentNodeId, autoUpdate, } from '@floating-ui/react';
import ReactDOM from 'react-dom';
import { CSSTransition } from 'react-transition-group';
import { useTheme } from 'styled-components';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import Button from '../../Controls/Button';
import Icons from '../../Elements/Icons';
import { ANIMATION_DURATION_MS } from './constants';
import Item from './Item';
import { ButtonComponent, ButtonWrapper, MeasureProbe, SubmenuTrigger, Wrapper } from './styled';
import { TheadButton } from './TheadButton';
function selectPlacement(align, isSubmenu) {
    if (isSubmenu) {
        return align === 'left' ? 'left-start' : 'right-start';
    }
    switch (align) {
        case 'right':
            return 'bottom-start';
        case 'left':
            return 'bottom-end';
        default:
            return 'bottom-start';
    }
}
const DropdownList = ({ id, className, dataTestId, children, variant = 'edit', disabled = false, buttonComponent, inThead, align = 'left', zIndex = 3, arrow = true, minWidth = true, isSubmenu = false, onVisibleChange, trackHiddenReference, maxHeight, title, }) => {
    const theme = useTheme();
    const [isShown, setIsShown] = React.useState(false);
    const isChildrenArrayEmpty = Array.isArray(children) ? children.every((c) => !!c === false) : false;
    const arrowRef = useRef(null);
    const nodeId = useFloatingNodeId();
    const { refs, floatingStyles, context, placement } = useFloating({
        nodeId,
        open: isShown,
        placement: selectPlacement(align, isSubmenu),
        whileElementsMounted: autoUpdate,
        onOpenChange: setIsShown,
        middleware: [
            offset(isSubmenu ? 1 : 5),
            flip({
                fallbackPlacements: isSubmenu
                    ? ['left-start', 'right-start', 'left-end', 'right-end']
                    : ['bottom-end', 'bottom-start', 'top-end', 'top-start'],
            }),
            // The arrow only renders in the non-submenu branch, so skip its middleware for submenus.
            ...(isSubmenu
                ? []
                : [
                    // eslint-disable-next-line react-hooks/refs
                    arrowMiddleware({
                        element: arrowRef,
                    }),
                ]),
        ],
    });
    useEffect(() => {
        onVisibleChange?.(isShown);
    }, [isShown, onVisibleChange]);
    const focus = useFocus(context, { enabled: !isSubmenu });
    // Submenus open on hover only: a click-opened submenu would not hover-close, so hovering another
    // section would leave two submenus open at once. Non-submenu behaviour is unchanged.
    const click = useClick(context, { enabled: !isSubmenu });
    const hover = useHover(context, { enabled: isSubmenu && !disabled, delay: { open: 150, close: 100 } });
    const { getReferenceProps, getFloatingProps } = useInteractions([click, focus, hover]);
    useDismiss(context, {
        enabled: isShown,
    });
    const probeRef = useRef(null);
    const [isSubmenuEmpty, setIsSubmenuEmpty] = React.useState(false);
    // A submenu whose items all render `null` at runtime must hide its trigger. While the submenu is
    // closed its items live in an off-screen (display:none) probe — mounted only when closed, so the
    // visible flyout never duplicates their data-testids. Observed because the leaf buttons toggle
    // from context (selection) without re-rendering this component.
    React.useLayoutEffect(() => {
        const node = probeRef.current;
        if (!isSubmenu || isShown || !node) {
            return;
        }
        const update = () => setIsSubmenuEmpty(node.childElementCount === 0);
        update();
        const observer = new MutationObserver(update);
        observer.observe(node, { childList: true, subtree: true });
        return () => observer.disconnect();
    }, [isSubmenu, isShown]);
    const isTop = placement.startsWith('top');
    const stopPropagation = useCallbackRef((evt) => evt.stopPropagation());
    if (isChildrenArrayEmpty) {
        return null;
    }
    return (_jsxs(FloatingNode, { id: nodeId, children: [isSubmenu
                ? isShown &&
                    ReactDOM.createPortal(_jsx(Wrapper, { ref: refs.setFloating, ...getFloatingProps(), style: floatingStyles, $visible: isShown, $isTop: isTop, $zIndex: zIndex, $minWidth: minWidth, $trackHiddenReference: trackHiddenReference, $maxHeight: maxHeight, children: children }), document.body)
                : ReactDOM.createPortal(_jsx(CSSTransition, { in: isShown, transitionAppear: true, timeout: ANIMATION_DURATION_MS, unmountOnExit: true, children: _jsxs(Wrapper
                    // eslint-disable-next-line react-hooks/refs
                    , { 
                        // eslint-disable-next-line react-hooks/refs
                        ref: refs.setFloating, ...getFloatingProps(), style: floatingStyles, $visible: isShown, $isTop: isTop, $zIndex: zIndex, $minWidth: minWidth, $trackHiddenReference: trackHiddenReference, $maxHeight: maxHeight, children: [arrow && _jsx(FloatingArrow, { ref: arrowRef, context: context, fill: theme.colors.grey00_1, width: 11, height: 5 }), children] }) }), document.body), isSubmenu && !isShown && (_jsx(MeasureProbe, { ref: probeRef, "aria-hidden": true, children: children })), _jsxs(ButtonWrapper, { onClick: stopPropagation, $block: isSubmenu, children: [isSubmenu && buttonComponent && !isSubmenuEmpty && (_jsx(SubmenuTrigger, { id: id, 
                        // eslint-disable-next-line react-hooks/refs
                        ref: refs.setReference, ...getReferenceProps(), className: className, "data-testid": dataTestId, children: buttonComponent })), !isSubmenu && inThead && (_jsx(TheadButton, { id: id, 
                        // eslint-disable-next-line react-hooks/refs
                        ref: refs.setReference, ...getReferenceProps(), className: className, "data-testid": dataTestId, variant: variant, disabled: disabled, square: true, active: isShown, children: _jsx(Icons.Angle, { size: 16, color: theme.colors.grey00_1 }) })), !isSubmenu && buttonComponent && !inThead && (_jsx(ButtonComponent, { ref: refs.setReference, ...getReferenceProps(), children: buttonComponent })), !isSubmenu && !buttonComponent && !inThead && (_jsxs(Button, { id: id, className: className, "data-testid": dataTestId, variant: variant, disabled: disabled, square: !title, 
                        // eslint-disable-next-line react-hooks/refs
                        ref: refs.setReference, ...getReferenceProps(), children: [title && _jsx("span", { children: title }), !title && _jsx(Icons.Angle, { size: 16, color: theme.colors.grey00_2, direction: isShown ? 'top' : 'bottom' })] }))] })] }));
};
const DropdownListMemo = React.memo(DropdownList);
const DropdownListWrapper = (props) => {
    // Register in a FloatingTree so a submenu's portalled flyout counts as "inside" the parent menu.
    // Without it, a pointerdown on a submenu item reads as an outside press and can dismiss the parent
    // before the item's onClick fires. The root dropdown creates the tree; nested dropdowns join it.
    const parentNodeId = useFloatingParentNodeId();
    if (parentNodeId === null) {
        return (_jsx(FloatingTree, { children: _jsx(DropdownListMemo, { ...props }) }));
    }
    return _jsx(DropdownListMemo, { ...props });
};
DropdownListWrapper.Item = Item;
export default DropdownListWrapper;
